import { useState } from 'react';

interface AdultHelpProps {
  dangerousTask: string;       // e.g. "Cắt lá chuối"
  adultAction: string;         // e.g. "Người lớn đang cắt lá chuối..."
  childSafeTask: string;       // e.g. "Em giữ lá chuối cho người lớn!"
  adultEmoji: string;          // e.g. "👩‍🍳"
  onAdultDone: () => void;     // called when adult finishes and child can continue
}

/* Reusable wrapper for dangerous steps that need adult help.
   Flow: Show warning → Choose help → Adult animation → Child safe task */
export default function AdultHelp({ dangerousTask, adultAction, childSafeTask, adultEmoji, onAdultDone }: AdultHelpProps) {
  const [stage, setStage] = useState<'choose' | 'self-warning' | 'adult-doing' | 'child-task'>('choose');
  const [adultProgress, setAdultProgress] = useState(0);

  const handleSelfTry = () => {
    setStage('self-warning');
    setTimeout(() => setStage('choose'), 2200);
  };

  const handleAskAdult = () => {
    setStage('adult-doing');
    let p = 0;
    const iv = setInterval(() => {
      p += 5;
      setAdultProgress(p);
      if (p >= 100) {
        clearInterval(iv);
        setTimeout(() => setStage('child-task'), 500);
      }
    }, 120);
  };

  const handleChildDone = () => {
    onAdultDone();
  };

  if (stage === 'choose') {
    return (
      <div className="flex flex-col items-center gap-5 p-6 bg-orange-50 rounded-3xl">
        <div className="text-5xl">⚠️</div>
        <div className="text-center">
          <h4 className="text-lg font-extrabold text-orange-700 mb-1">{dangerousTask} là việc nguy hiểm!</h4>
          <p className="text-sm text-orange-600">Em muốn làm thế nào?</p>
        </div>
        <div className="flex flex-col gap-3 w-full max-w-xs">
          <button
            onClick={handleAskAdult}
            className="py-4 bg-gradient-to-r from-green-500 to-emerald-400 text-white font-extrabold rounded-2xl cursor-pointer hover:opacity-90 whitespace-nowrap transition-all"
          >
            👨‍👩‍👧 Nhờ người lớn giúp
          </button>
          <button
            onClick={handleSelfTry}
            className="py-3 border-2 border-orange-300 text-orange-600 font-bold rounded-2xl cursor-pointer hover:bg-orange-50 whitespace-nowrap transition-all text-sm"
          >
            Tự làm
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'self-warning') {
    return (
      <div className="flex flex-col items-center gap-4 p-6 bg-red-50 rounded-3xl animate-pulse">
        <div className="text-6xl">🚫</div>
        <h4 className="text-lg font-extrabold text-red-700 text-center">Việc này cần có người lớn giúp để đảm bảo an toàn.</h4>
        <p className="text-sm text-red-600 text-center">Hãy nhờ người lớn nhé!</p>
      </div>
    );
  }

  if (stage === 'adult-doing') {
    return (
      <div className="flex flex-col items-center gap-5 p-6 bg-blue-50 rounded-3xl">
        <div className="text-7xl animate-bounce">{adultEmoji}</div>
        <p className="text-base font-extrabold text-blue-700 text-center">{adultAction}</p>
        <div className="w-full max-w-xs">
          <div className="flex justify-between text-xs text-blue-500 mb-1">
            <span>Đang làm...</span>
            <span>{adultProgress}%</span>
          </div>
          <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-400 to-sky-400 rounded-full transition-all duration-200"
              style={{ width: `${adultProgress}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  // child-task stage
  return (
    <div className="flex flex-col items-center gap-5 p-6 bg-green-50 rounded-3xl">
      <div className="flex items-center gap-3">
        <span className="text-5xl">🧒</span>
        <span className="text-3xl">+</span>
        <span className="text-5xl">{adultEmoji}</span>
      </div>
      <div className="text-center">
        <p className="text-sm text-gray-500 mb-1">Người lớn đã làm phần nguy hiểm xong!</p>
        <h4 className="text-lg font-extrabold text-green-700">{childSafeTask}</h4>
      </div>
      <button
        onClick={handleChildDone}
        className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-400 text-white font-extrabold rounded-2xl cursor-pointer hover:opacity-90 whitespace-nowrap transition-all"
      >
        ✅ Em làm được rồi!
      </button>
    </div>
  );
}
